import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from '@angular/core';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import { asText } from '@modules/common/utils/editor';
import { type Note } from '../types/note';
import { noteVersionError, NoteVersionsClient, type SavedNoteVersion } from './note-versions.client';

@Component({
  selector: 'app-note-version-preview',
  standalone: true,
  providers: [NoteVersionsClient],
  templateUrl: './note-version-preview.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NoteVersionPreviewComponent {
  public readonly note = input.required<Note>();
  public readonly version = input.required<number>();
  private readonly client = inject(NoteVersionsClient);
  private readonly space = inject(SpaceContextStore);
  protected readonly available = computed(
    () => !!this.note().id && this.space.insideTeam() && this.note().accountId === this.space.contextId(),
  );
  protected readonly saved = signal<SavedNoteVersion | null>(null);
  protected readonly loading = signal(false);
  protected readonly failure = signal<string | null>(null);
  private readonly retryVersion = signal(0);
  protected readonly editorBody = computed(() => {
    const body = this.saved()?.body;
    if (!body) return false;
    try {
      const parsed: unknown = JSON.parse(body);
      return !!parsed && typeof parsed === 'object' && 'blocks' in parsed && Array.isArray(parsed.blocks);
    } catch {
      return false;
    }
  });
  protected readonly renderedBody = computed(() => {
    const body = this.saved()?.body ?? '';
    // The existing converter removes non-content markup; interpolation keeps the result inert.
    return this.editorBody() ? asText(body) : body;
  });
  private readonly load = effect((onCleanup) => {
    const note = this.note();
    const version = this.version();
    const available = this.available();
    this.retryVersion();
    this.saved.set(null);
    this.failure.set(null);
    this.loading.set(available);
    if (!available) return;
    const current = (): boolean => this.note() === note && this.version() === version && this.available();
    const subscription = this.client.read(note, version).subscribe({
      next: (saved) => {
        if (!current()) return;
        if (saved.version !== version) this.failure.set('Version not found');
        else this.saved.set(saved);
        this.loading.set(false);
      },
      error: (error: unknown) => {
        if (current()) {
          this.failure.set(noteVersionError(error));
          this.loading.set(false);
        }
      },
    });
    onCleanup(() => subscription.unsubscribe());
  });
  protected retry(): void {
    this.retryVersion.update((value) => value + 1);
  }
}
